import React from 'react';
import Breadcrumb from '../components/Breadcrumb';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, FileText, Presentation, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

const TeachingKits = () => {
  const handleDownload = (type) => {
    // Coming soon notification
    toast.info(`${type} - Coming Soon!`, {
      description: 'Downloads will be available in the next update.'
    });
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumb items={[{ label: "Learner's Kits", path: '/teaching-kits' }]} />

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Learner's Kits</h1>
          <p className="text-xl text-gray-300">
            Comprehensive educational materials: one-pagers, slide decks, and worksheets—all dynamically generated from our content.
          </p>
          <div className="mt-4 inline-block bg-amber-500/20 border border-amber-500/50 rounded-lg px-4 py-2">
            <p className="text-amber-300 font-semibold">📦 Downloads Coming Soon</p>
          </div>
        </div>

        {/* One-Pagers */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-6">One-Page Summaries</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-gray-900/50 border-gray-800 card-hover">
              <CardHeader>
                <FileText className="w-12 h-12 text-emerald-500 mb-4" />
                <CardTitle className="text-white">CSP One-Pager</CardTitle>
                <CardDescription>
                  Core mechanics, formulas, analogies, and risks—all on one page.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleDownload('CSP One-Pager')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF (Coming Soon)
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-gray-800 card-hover">
              <CardHeader>
                <FileText className="w-12 h-12 text-emerald-500 mb-4" />
                <CardTitle className="text-white">CC One-Pager</CardTitle>
                <CardDescription>
                  Covered calls explained with insurance framing and key calculations.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleDownload('CC One-Pager')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF (Coming Soon)
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-gray-800 card-hover">
              <CardHeader>
                <FileText className="w-12 h-12 text-emerald-500 mb-4" />
                <CardTitle className="text-white">Wheel One-Pager</CardTitle>
                <CardDescription>
                  The complete Wheel strategy cycle with common pitfalls.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleDownload('Wheel One-Pager')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF (Coming Soon)
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Slide Deck */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-6">Presentation Slide Deck</h2>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Presentation className="w-12 h-12 text-emerald-500 mb-4" />
                  <CardTitle className="text-white mb-2">Options Analogies Master Deck</CardTitle>
                  <CardDescription className="text-base">
                    10-15 professionally designed slides covering CSP, CC, Wheel, and top analogies. Perfect for classroom instruction or self-study.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-6">
                <p className="text-gray-400 flex items-center">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mr-3" />
                  Insurance core metaphor explained
                </p>
                <p className="text-gray-400 flex items-center">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mr-3" />
                  Key analogies with visual aids
                </p>
                <p className="text-gray-400 flex items-center">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mr-3" />
                  Risk callouts and formulas
                </p>
                <p className="text-gray-400 flex items-center">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mr-3" />
                  Interactive exercise prompts
                </p>
              </div>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => handleDownload('Master Slide Deck')}
              >
                <Download className="w-4 h-4 mr-2" />
                Download PowerPoint (Coming Soon)
              </Button>
            </CardContent>
          </Card>
        </section>

        {/* Worksheet */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-6">Underwriter's Worksheet</h2>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader>
              <ClipboardList className="w-12 h-12 text-emerald-500 mb-4" />
              <CardTitle className="text-white mb-2">"Underwrite Your Strike" Checklist</CardTitle>
              <CardDescription className="text-base">
                Systematic framework for selecting strikes and managing risk—just like an insurance underwriter.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div>
                  <h4 className="text-white font-semibold mb-2">Includes:</h4>
                  <ul className="space-y-2 text-gray-400 text-sm">
                    <li>• Investment thesis worksheet</li>
                    <li>• Strike selection criteria</li>
                    <li>• Risk tolerance assessment</li>
                    <li>• Entry/exit planning template</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-2">Covers:</h4>
                  <ul className="space-y-2 text-gray-400 text-sm">
                    <li>• Earnings calendar checks</li>
                    <li>• Volatility considerations</li>
                    <li>• Capital allocation planning</li>
                    <li>• Assignment scenario prep</li>
                  </ul>
                </div>
              </div>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => handleDownload('Underwriter Worksheet')}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Worksheet (Coming Soon)
              </Button>
            </CardContent>
          </Card>
        </section>

        {/* Usage Note */}
        <div className="glass rounded-xl p-6 text-center">
          <p className="text-gray-300">
            All materials are <strong className="text-white">free for educational use</strong>. Share with students, colleagues, or study groups. Remember: education only—not financial advice.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TeachingKits;